import React, { useEffect, useRef } from "react";
import { AnimatePresence, animate, motion, useReducedMotion } from "framer-motion";
import { CalendarContainer } from "react-datepicker";
import { components as selectComponents } from "react-select";

import { Collapse, DURATION, EASE_OUT } from "../../motion";
import { ChevronRightIcon } from "../../icons";
import "./formControls.css";
import { BROWSER_AUTOFILL_OFF } from "../../../utils/autofill";

/**
 * Small motion pieces shared by the booking search forms
 * (SpecifiedDestinationForm, ByTheHourForm) so every field opens, closes and
 * reports errors the same way.
 *
 * They are declared here, at module level, on purpose: react-datepicker and
 * react-select render these as component *types*. A component defined inside
 * a form's render would be a brand-new type on every re-render, remounting
 * the popup and replaying its entrance animation each time.
 */

/**
 * A validation message that eases open under its field instead of popping in,
 * so the fields below glide down rather than jump.
 *
 * <FieldError show={!formDataValidation.pax}>{t("...")}</FieldError>
 */
export const FieldError = ({ show, className = "text-danger", children }) => (
  <Collapse isOpen={show} duration={DURATION.fast}>
    <p className={className}>{children}</p>
  </Collapse>
);

// Brief gold ring on an input once a value is picked, so the customer sees
// the choice "land" in the field. Starts at the brand gold and fades to clear.
const SELECTED_RING_FROM = "0 0 0 3px rgba(219, 190, 120, 0.55)";
const SELECTED_RING_TO = "0 0 0 3px rgba(219, 190, 120, 0)";

/**
 * Plays the gold "picked" ring on an input (location, date, time). The inline
 * box-shadow is removed afterwards so the input's normal focus styles apply.
 */
export const playSelectedRing = (inputElement, shouldReduceMotion) => {
  if (!inputElement || shouldReduceMotion) return;
  animate(
    inputElement,
    { boxShadow: [SELECTED_RING_FROM, SELECTED_RING_TO] },
    { duration: DURATION.slow, ease: EASE_OUT }
  ).then(() => {
    inputElement.style.boxShadow = "";
  });
};

// How far above the current time the time list starts before gliding to it.
const TIME_LIST_GLIDE_DISTANCE = 60;

/**
 * The date / time picker popup. Pass it as `calendarContainer` on <DatePicker>
 * (AnimatedDatePicker does this for you).
 *
 * It fades and drops in slightly; the picker positions the popup on an outer
 * wrapper, so moving this inner element never fights that positioning.
 *
 * In a time picker, react-datepicker jumps the list straight to the current
 * time when it opens. We start it a little above and let it glide there, so
 * the customer sees where in the day they are.
 */
export const AnimatedCalendarContainer = ({ className, children }) => {
  const shouldReduceMotion = useReducedMotion();
  const contentRef = useRef(null);

  useEffect(() => {
    // The time list has already scrolled itself by now: child effects run
    // before this parent's effect.
    const timeList = contentRef.current?.querySelector(".react-datepicker__time-list");
    if (!timeList || shouldReduceMotion) return undefined;

    const targetScrollTop = timeList.scrollTop;
    if (targetScrollTop === 0) return undefined;

    const gliding = animate(Math.max(targetScrollTop - TIME_LIST_GLIDE_DISTANCE, 0), targetScrollTop, {
      duration: DURATION.base,
      ease: EASE_OUT,
      onUpdate: (scrollTop) => {
        timeList.scrollTop = scrollTop;
      },
    });
    return () => gliding.stop();
  }, [shouldReduceMotion]);

  return (
    <CalendarContainer className={className}>
      <motion.div
        ref={contentRef}
        style={{ position: "relative" }}
        initial={{ opacity: 0, y: shouldReduceMotion ? 0 : -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION.fast, ease: EASE_OUT }}
      >
        {children}
      </motion.div>
    </CalendarContainer>
  );
};

/**
 * The option list of a react-select dropdown. Add it to the Select's
 * `components` prop as `Menu: AnimatedSelectMenu`.
 */
export const AnimatedSelectMenu = (props) => {
  const shouldReduceMotion = useReducedMotion();
  return (
    <selectComponents.Menu {...props}>
      <motion.div
        initial={{ opacity: 0, y: shouldReduceMotion ? 0 : -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION.fast, ease: EASE_OUT }}
      >
        {props.children}
      </motion.div>
    </selectComponents.Menu>
  );
};

/**
 * The gold arrow on the right of a dropdown. It turns to point up while the
 * menu is open. Add it as `DropdownIndicator: AnimatedSelectIndicator`.
 */
export const AnimatedSelectIndicator = (props) => {
  const shouldReduceMotion = useReducedMotion();
  const isMenuOpen = props.selectProps.menuIsOpen;
  return (
    <selectComponents.DropdownIndicator {...props}>
      <motion.span
        className="tw:inline-flex tw:pr-2"
        animate={{ rotate: isMenuOpen ? -90 : 90 }}
        transition={shouldReduceMotion ? { duration: 0 } : { duration: DURATION.fast, ease: EASE_OUT }}
      >
        <ChevronRightIcon width={7} height={10} color="#DBBE78" />
      </motion.span>
    </selectComponents.DropdownIndicator>
  );
};

/**
 * One option in a dropdown, with a small gold tick next to the chosen one.
 * Add it as `Option: LuxurySelectOption`; the look is in formControls.css.
 */
export const LuxurySelectOption = (props) => (
  <selectComponents.Option {...props}>
    <span>{props.children}</span>
    <AnimatePresence initial={false}>
      {props.isSelected && (
        <motion.svg
          key="check"
          className="tlf-select-option__check"
          width="14"
          height="11"
          viewBox="0 0 14 11"
          fill="none"
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.6 }}
          transition={{ duration: DURATION.fast, ease: EASE_OUT }}
        >
          <path d="M1 5.5L5 9.5L13 1.5" stroke="#B89A55" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </motion.svg>
      )}
    </AnimatePresence>
  </selectComponents.Option>
);

/**
 * The passenger / luggage counter panel: drops in under the field and fades
 * back out when closed. Wrap the conditional <PassengerCard> in
 * <AnimatePresence> so the closing animation gets to play.
 */
export const panelMotionProps = (shouldReduceMotion) => ({
  initial: { opacity: 0, y: shouldReduceMotion ? 0 : -8, scale: shouldReduceMotion ? 1 : 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: shouldReduceMotion ? 0 : -8, scale: shouldReduceMotion ? 1 : 0.98 },
  transition: { duration: DURATION.fast, ease: EASE_OUT },
  style: { transformOrigin: "top center" },
});

/**
 * Cross-fades a button's label and its loading spinner instead of swapping
 * them instantly. The button keeps a fixed height, so nothing shifts.
 *
 * <ButtonLoadingSwap isLoading={isLoading} spinner={<Spinner />}>Search</ButtonLoadingSwap>
 */
export const ButtonLoadingSwap = ({ isLoading, spinner, children }) => (
  <AnimatePresence mode="wait" initial={false}>
    <motion.span
      key={isLoading ? "loading" : "label"}
      className="tw:flex tw:items-center tw:justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15, ease: EASE_OUT }}
    >
      {isLoading ? spinner : children}
    </motion.span>
  </AnimatePresence>
);

/** Gentle hover lift and press for the big "Search available vehicles" button. */
export const searchButtonMotionProps = (isDisabled, shouldReduceMotion) =>
  isDisabled || shouldReduceMotion
    ? {}
    : {
        whileHover: { scale: 1.01 },
        whileTap: { scale: 0.98 },
        transition: { duration: 0.15, ease: EASE_OUT },
      };

/**
 * Modal fade (backdrop) and pop (dialog), used by the "same location" alert.
 */
export const modalBackdropMotionProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: DURATION.fast, ease: EASE_OUT },
};

export const modalDialogMotionProps = (shouldReduceMotion) => ({
  initial: { opacity: 0, y: shouldReduceMotion ? 0 : 12, scale: shouldReduceMotion ? 1 : 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: shouldReduceMotion ? 0 : 8, scale: shouldReduceMotion ? 1 : 0.98 },
  transition: { duration: DURATION.fast, ease: EASE_OUT },
});

/**
 * Round "-" / "+" counter button feel, matching the Meet & Assist inquiry
 * form: a slight lift on hover and a squeeze on press. A disabled button
 * stays still, so it doesn't look clickable.
 *
 * <motion.button {...stepButtonMotionProps(isDisabled, shouldReduceMotion)} />
 */
export const stepButtonMotionProps = (isDisabled, shouldReduceMotion) =>
  isDisabled || shouldReduceMotion
    ? {}
    : {
        whileHover: { scale: 1.06 },
        whileTap: { scale: 0.88 },
        transition: { duration: 0.15, ease: EASE_OUT },
      };

// How far the number travels when it changes, in px.
const COUNT_SLIDE_DISTANCE = 6;

/**
 * The number between a counter's "-" and "+" buttons. It stays an <input> so
 * it keeps the existing styling, but when the value changes the number slides
 * in from the direction of the change -- up from below when it goes up, down
 * from above when it goes down -- like SwapText in the inquiry form.
 *
 * Takes the same props as an <input>.
 */
export const AnimatedCountInput = (inputProps) => {
  const shouldReduceMotion = useReducedMotion();
  const inputRef = useRef(null);
  const previousValueRef = useRef(inputProps.value);

  useEffect(() => {
    const previousValue = Number(previousValueRef.current) || 0;
    const currentValue = Number(inputProps.value) || 0;
    previousValueRef.current = inputProps.value;

    const inputElement = inputRef.current;
    if (!inputElement || currentValue === previousValue) return undefined;

    const slideFrom = currentValue > previousValue ? COUNT_SLIDE_DISTANCE : -COUNT_SLIDE_DISTANCE;
    const sliding = animate(
      inputElement,
      { opacity: [0, 1], y: [shouldReduceMotion ? 0 : slideFrom, 0] },
      { duration: DURATION.fast * 0.7, ease: EASE_OUT }
    );
    // A quick second tap stops this slide and starts a fresh one.
    return () => sliding.stop();
  }, [inputProps.value, shouldReduceMotion]);

  return <input ref={inputRef} autoComplete={BROWSER_AUTOFILL_OFF} {...inputProps} />;
};
